/**
 * Table.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef, useState } from "react";
import { getBaseClass } from "./baseClass";
import { TableContext } from "./TableContext";
import { TableHeader } from "./TableHeader";
import { ITableColumn, ITableColumnConfig, ITableState } from "./TableTypes";
import { useDeepCompareMemo } from "use-deep-compare";

export interface ITableProps {
	columns: ITableColumn[];
	state: ITableState;
	onSortChange?: (columnId: string) => void;
	rowSelectable?: boolean;
	onRowClick?: (id: string) => void;
}

export const Table: React.FC<ITableProps> = ({
	children,
	columns,
	state: tableState,
	onSortChange,
	rowSelectable,
	onRowClick
}) => {
	const theadRef = useRef<HTMLTableSectionElement>();
	const [ headerHeight, setHeaderHeight ] = useState<number | undefined>();

	const baseClass = getBaseClass();
	baseClass.add("edt-base");

	const selectedRows = tableState?.selectionIds || [];

	const columnsConfig = useDeepCompareMemo(
		() =>
			columns.reduce((acc, column) => {
				const sortConf = tableState?.sort.find((sortConf) => sortConf.columnId === column.id);

				acc.set(column.id, {
					id: column.id,
					width: column.width,
					sortable: column.sortable,
					sortActive: !!sortConf,
					sortDirection: sortConf?.direction,
					headerAlign: column.headerAlign
				});
				return acc;
			}, new Map<string, ITableColumnConfig>()),
		[ columns, tableState ]
	);

	const cols = [];
	const ths = [];

	columns.forEach((column) => {
		const columnConfig = columnsConfig.get(column.id);

		cols.push(<col key={`col-${columnConfig.id}`} style={{ width: columnConfig.width }} />);
		ths.push(
			<TableHeader
				key={`header-${columnConfig.id}`}
				columnId={columnConfig.id}
				sortable={columnConfig.sortable}
				onSort={onSortChange}
				sortActive={!!columnConfig.sortActive}
				sortDirection={columnConfig?.sortDirection}
				align={columnConfig.headerAlign}
				scope="col"
			>
				{column.label}
			</TableHeader>
		);
	});

	useEffect(() => {
		const actualHeaderHeight = theadRef.current?.offsetHeight;
		if (headerHeight !== actualHeaderHeight) {
			setHeaderHeight(actualHeaderHeight);
		}
	}, [ theadRef.current ]);

	return (
		<TableContext.Provider
			value={{ columnsConfig, selectedRows, rowSelectable, onRowClick, headerHeight }}
		>
			<div className={baseClass.toClassName()}>
				<table>
					<colgroup>{cols}</colgroup>
					<thead ref={theadRef}>
						<tr>{ths}</tr>
					</thead>
					<tbody>{children}</tbody>
				</table>
			</div>
		</TableContext.Provider>
	);
};
